import { defineComponent, h, onBeforeUnmount, onMounted, ref, shallowRef } from 'vue'

import { QImg } from 'quasar'

import { useVm } from '../../../composables/useVm'

/**
 * The app's image. It takes QImg's props by extending it — `src`, `alt`, `ratio`, the spinner and
 * placeholder options — and replaces its render with one of two: a QImg it configures itself, or a plain
 * `img` when `native` is set. `extends` is what the class's `extends QImg` compiled to.
 *
 * Its real job is `modifiedSrc`: turning a stored path into a URL that will actually load, per build mode
 * and per platform.
 */

/**
 * Absolute URLs pointing at one of these hosts get rewritten to `/img-proxy/<host>/<path>` so the request
 * is same-origin. This keeps consent/tracker blockers (e.g. Termly auto-block under CCPA) from
 * intercepting the request as third-party, which otherwise leaves QImg's internal `Image()` loader stuck
 * in the loading state forever.
 *
 * Each host added here must also be allowlisted in the `/img-proxy/` nginx location regex in
 * dev-ops/.../sites-enabled/*.booksprout.co.conf.
 */
const PROXIED_EXTERNAL_HOSTS: ReadonlySet<string> = new Set([
  'm.media-amazon.com'
])

export default defineComponent({
  name: 'BsImg',

  extends: QImg,

  props: {
    native: { type: Boolean },
    large: { type: Boolean },
    // Declared here because Quasar 2's QImg dropped it — `extends: QImg` no longer supplies it.
    // The render maps it onto QImg's `fit`.
    contain: { type: Boolean },
    // 'lazy' defers the actual image fetch (and forwards `loading="lazy"` to native mode)
    // until the element scrolls within ~200px of the viewport. Critical for long lists
    // (e.g. 100+ claims on a campaign view) where eagerly loading every image floods
    // the network and image-decode pipeline on first paint.
    loading: { type: String as unknown as () => 'lazy' | 'eager', default: 'eager' }
  },

  setup (props) {
    const vm = useVm()

    // Reactive — flipped to true when the IntersectionObserver fires. While false
    // we pass `src: undefined` to QImg so its mounted/__load() short-circuits and
    // the placeholder renders instead.
    const bsImgVisible = ref(props.loading !== 'lazy')
    // shallowRef, not ref: Vue 3's `ref` deep-converts an object into a reactive proxy, so the
    // observer we handed out would not be identity-equal to the one we hold — and every property
    // read on it would go through a proxy. Only the reference itself needs to be reactive.
    const bsImgObserver = shallowRef<IntersectionObserver | null>(null)

    onMounted(() => {
      if (props.loading !== 'lazy' || typeof IntersectionObserver === 'undefined') {
        return
      }

      bsImgObserver.value = new IntersectionObserver(entries => {
        if (entries.some(e => e.isIntersecting)) {
          bsImgVisible.value = true
          bsImgObserver.value?.disconnect()
          bsImgObserver.value = null
        }
      }, { rootMargin: '200px' })

      bsImgObserver.value.observe(vm.$el)
    })

    onBeforeUnmount(() => {
      bsImgObserver.value?.disconnect()
      bsImgObserver.value = null
    })

    return {
      bsImgVisible,
      bsImgObserver
    }
  },

  computed: {
    modifiedSrc (): string | undefined {
      if (!this.src) {
        return void 0
      }

      let src = this.src + ''

      // Rewrite allowlisted external URLs to the same-origin proxy path.
      // Skip inside Capacitor: there is no nginx in the native shell, so a
      // relative `/img-proxy/...` request resolves against the local app bundle
      // and 404s. Termly does not run on native either, so the original URL is
      // safe to use directly.
      // Capacitor 3+ exposes `isNativePlatform()`; older versions used the
      // `isNative` boolean. Check both, plus `getPlatform() !== 'web'` as a
      // belt-and-braces fallback.
      const cap = typeof window !== 'undefined' ? window.Capacitor : void 0
      const isNative = !!cap && (
        (typeof cap.isNativePlatform === 'function' && cap.isNativePlatform()) ||
        cap.isNative === true ||
        (typeof cap.getPlatform === 'function' && cap.getPlatform() !== 'web')
      )
      const externalMatch = /^https?:\/\/([^/?#]+)([^?#]*)/i.exec(src)
      if (externalMatch !== null) {
        const host = externalMatch[1].toLowerCase()
        if (!isNative && PROXIED_EXTERNAL_HOSTS.has(host)) {
          const path = externalMatch[2] || '/'
          return `/img-proxy/${host}${path}`
        }
        // Not allowlisted (or running natively) — leave the URL untouched rather
        // than silently proxying arbitrary third-party content.
        return src
      }

      // return this.src
      let prefix
      // Don't mess with full URLS i.e imported URLs with an image hosted on amazon
      if (src.startsWith('cdn') || src.startsWith('/cdn') || src.startsWith('\\cdn')) {
        if (['beta'].includes(process.env.BUILD_MODE + '')) {
          prefix = 'https://beta.booksprout.co'
        } else if (['sandbox'].includes(process.env.BUILD_MODE + '')) {
          prefix = 'https://sandbox.booksprout.co'
        } else if (['master'].includes(process.env.BUILD_MODE + '')) {
          prefix = 'https://booksprout.co'
        }

        if (prefix && !src.startsWith('/') && !src.startsWith('\\')) {
          prefix += '/'
        }

        // Books can have 2 thumbnail sizes so check to see if we want the large or normal sized one.
        if (this.large) {
          if (src.indexOf('uploads\\book\\thumbnails\\') > -1) {
            src = src.replace('uploads\\book\\thumbnails\\', 'uploads\\book\\thumbnails\\415\\')
          }

          if (src.indexOf('uploads/book/thumbnails/') > -1) {
            src = src.replace('uploads/book/thumbnails/', 'uploads/book/thumbnails/415/')
          }
        }
      }

      // normalize path for web
      src = src.replace(/\\/g, '/')

      return prefix ? `${prefix}${src}` : src
    },

    effectiveSrc (): string | undefined {
      return this.bsImgVisible ? this.modifiedSrc : void 0
    }
  },

  render () {
    if (!this.native) {
      return h(QImg, {
        placeholderSrc: this.placeholderSrc,
        src: this.effectiveSrc,
        width: this.width,
        height: this.height,
        sizes: this.sizes,
        srcset: this.srcset,
        imgClass: this.imgClass,
        imgStyle: this.imgStyle,
        spinnerColor: this.spinnerColor,
        spinnerSize: this.spinnerSize,
        ratio: this.ratio,
        alt: this.alt,
        // Quasar 2 dropped QImg's `contain` for the CSS-valued `fit`; 'cover' is its default.
        fit: this.contain ? 'contain' : void 0
      })
    } else {
      const onImgErrorSmall = (event: any) => {
        event.target.src = this.placeholderSrc
        event.target.onerror = ''
      }

      return h('img', {
        src: this.effectiveSrc,
        width: this.width,
        height: this.height,
        alt: this.alt,
        loading: this.loading,
        onError: onImgErrorSmall
      })
    }
  }
})
